"use client";

import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Shared bootstrap for motion (shared across non-home pages):
 *  - parallax of cosmos nebulas
 *  - reveals (.reveal / .reveal-tilt / .reveal-fade)
 *  - magnetic CTAs (.btn-magnetic)
 *  - scroll progress bar
 *  - spotlight + 3D tilt for cards (selector configurable)
 */
function bootCommon(opts: { tiltSelector?: string } = {}) {
  const tiltSelector = opts.tiltSelector ?? ".svc-card";

  gsap.utils.toArray<HTMLElement>(".reveal").forEach((el) => {
    gsap.from(el, {
      opacity: 0, y: 40, duration: 0.9, ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" },
    });
  });
  gsap.utils.toArray<HTMLElement>(".reveal-tilt").forEach((el) => {
    gsap.from(el, {
      opacity: 0, y: 60, rotateX: -12, duration: 1, ease: "power3.out", transformOrigin: "center top",
      scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" },
    });
  });
  gsap.utils.toArray<HTMLElement>(".reveal-fade").forEach((el) => {
    gsap.from(el, {
      opacity: 0, duration: 1.2, ease: "power2.out",
      scrollTrigger: { trigger: el, start: "top 92%", toggleActions: "play none none none" },
    });
  });

  gsap.to(".cosmos__nebula--a", { y: 200, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });
  gsap.to(".cosmos__nebula--b", { y: -260, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });
  gsap.to(".cosmos__nebula--c", { y: 180, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });

  // Tilt + spotlight on cards
  document.querySelectorAll<HTMLElement>(tiltSelector).forEach((card) => {
    const onMove = (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      card.style.setProperty("--mx", x * 100 + "%");
      card.style.setProperty("--my", y * 100 + "%");
      gsap.to(card, { rotateX: (0.5 - y) * 6, rotateY: (x - 0.5) * 8, duration: 0.4, ease: "power2.out" });
    };
    const onLeave = () => gsap.to(card, { rotateX: 0, rotateY: 0, duration: 0.6, ease: "power3.out" });
    card.addEventListener("mousemove", onMove);
    card.addEventListener("mouseleave", onLeave);
  });

  // Magnetic CTAs
  document.querySelectorAll<HTMLElement>(".btn-magnetic").forEach((btn) => {
    const onMove = (e: MouseEvent) => {
      const r = btn.getBoundingClientRect();
      const x = e.clientX - r.left - r.width / 2;
      const y = e.clientY - r.top - r.height / 2;
      gsap.to(btn, { x: x * 0.18, y: y * 0.3, duration: 0.4, ease: "power2.out" });
    };
    const onLeave = () => gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: "elastic.out(1, 0.4)" });
    btn.addEventListener("mousemove", onMove);
    btn.addEventListener("mouseleave", onLeave);
  });

  // Scroll progress
  const progress = document.querySelector<HTMLElement>(".scroll-progress");
  if (progress) {
    ScrollTrigger.create({
      start: 0,
      end: "max",
      onUpdate: (self) => { progress.style.width = self.progress * 100 + "%"; },
    });
  }
}

function applyReducedMotionFallback() {
  document.querySelectorAll<HTMLElement>(".counter").forEach((el) => {
    const to = Number(el.dataset.to || "0");
    el.textContent = (el.dataset.prefix || "") + to.toLocaleString("pt-BR") + (el.dataset.suffix || "");
  });
  document.querySelectorAll<HTMLElement>(".console--servicos, .console--svc-detail").forEach((el) => {
    el.style.opacity = "1";
  });
}

/**
 * Motion for the /servicos HUB page.
 *  - hero stagger (.servicos-hero)
 *  - timeline-style console fade-in
 *  - rotating decorative orbits
 *  - common reveals/tilt/magnetic/progress + parallax
 *  - hue-shift on the cosmos as the user scrolls past the first pilar
 */
export function ServicosMotion() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      applyReducedMotionFallback();
      return;
    }
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      // Hero intro
      const heroTl = gsap.timeline({ defaults: { ease: "power3.out" }, delay: 0.2 });
      heroTl
        .from(".servicos-hero .crumb", { opacity: 0, y: 8, duration: 0.5 })
        .from(".servicos-hero .hero__telemetry", { opacity: 0, y: 10, duration: 0.7 }, "-=0.2")
        .from(".servicos-hero .hero__title .char", { yPercent: 110, opacity: 0, duration: 0.7, stagger: 0.012, ease: "power3.out" }, "-=0.4")
        .from(".servicos-hero .hero__sub", { opacity: 0, y: 10, duration: 0.6 }, "-=0.3")
        .from(".servicos-hero .hero__ctas", { opacity: 0, y: 10, duration: 0.6 }, "-=0.4")
        .from(".servicos-hero .hero__micro", { opacity: 0, duration: 0.5 }, "-=0.3");

      // Mission console (pilares)
      gsap.from(".console--servicos", { opacity: 0, x: 30, duration: 1, delay: 0.9, ease: "power3.out" });
      gsap.from(".console--servicos .console__row", { opacity: 0, x: 30, duration: 0.5, stagger: 0.12, ease: "power2.out", delay: 1.1 });

      // Orbits
      gsap.to(".servicos-hero .orbit--1", { rotation: 360, duration: 40, repeat: -1, ease: "none", transformOrigin: "50% 50%" });
      gsap.to(".servicos-hero .orbit--2", { rotation: -360, duration: 64, repeat: -1, ease: "none", transformOrigin: "50% 50%" });

      // Stage parallax
      gsap.to(".servicos-hero__stage", {
        y: -80,
        scrollTrigger: { trigger: ".servicos-hero", start: "top top", end: "bottom top", scrub: 1 },
      });

      bootCommon({ tiltSelector: ".svc-card" });

      // Hue shift as user passes the first pilar
      const firstPilar = document.querySelector<HTMLElement>(".pillar-section");
      if (firstPilar) {
        gsap.to(".cosmos", {
          filter: "hue-rotate(25deg)",
          scrollTrigger: { trigger: firstPilar, start: "top center", end: "bottom center", scrub: true },
        });
      }

      requestAnimationFrame(() => ScrollTrigger.refresh());
    });

    return () => ctx.revert();
  }, []);

  return null;
}

/**
 * Motion for the /servicos/[slug] detail page.
 *  - hero stagger (.svc-hero)
 *  - detail console (.console--svc-detail) fade-in
 *  - common reveals/tilt/magnetic/progress + parallax
 */
export function ServicoDetailMotion() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      applyReducedMotionFallback();
      return;
    }
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const heroTl = gsap.timeline({ defaults: { ease: "power3.out" }, delay: 0.2 });
      heroTl
        .from(".crumb", { opacity: 0, y: 8, duration: 0.5 })
        .from(".hero__telemetry", { opacity: 0, y: 10, duration: 0.7 }, "-=0.2")
        .from(".hero__title .char", { yPercent: 110, opacity: 0, duration: 0.7, stagger: 0.012, ease: "power3.out" }, "-=0.4")
        .from(".hero__sub", { opacity: 0, y: 10, duration: 0.6 }, "-=0.3")
        .from(".hero__ctas", { opacity: 0, y: 10, duration: 0.6 }, "-=0.4");

      // Detail console
      gsap.from(".console--svc-detail", { opacity: 0, x: 30, duration: 1, delay: 0.8, ease: "power3.out" });
      gsap.from(".console--svc-detail .console__row", { opacity: 0, x: 30, duration: 0.5, stagger: 0.1, ease: "power2.out", delay: 1.0 });

      // Stage parallax
      gsap.to(".svc-hero__sym", {
        y: -40,
        scrollTrigger: { trigger: ".svc-hero", start: "top top", end: "bottom top", scrub: 1 },
      });

      bootCommon({ tiltSelector: ".step, .case" });

      requestAnimationFrame(() => ScrollTrigger.refresh());
    });

    return () => ctx.revert();
  }, []);

  return null;
}
